import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Empty, Field, Input, Select } from "./ui";
import { toast } from "sonner";
import { UserPlus, Search, KeyRound, Power, Pencil, ShieldCheck, Copy, RefreshCw, History, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import StaffPortalAccess from "./StaffPortalAccess";
import { fmtDateTime } from "../../lib/format";

const emptyProv = {
  email: "", name: "", phone: "", role_id: "",
  location_id: "", region_id: "", employee_id: "",
  pin: "", password: "",
};

function StatBox({ label, value, tint, testId }) {
  return (
    <div className="rounded-2xl border p-4 flex flex-col gap-1" style={{ borderColor: HC.border, background: HC.panel }} data-testid={testId}>
      <div className="text-[11px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{label}</div>
      <div className="font-brand text-3xl font-bold" style={{ color: tint || HC.text }}>{value}</div>
    </div>
  );
}

function PermMatrix({ role }) {
  const perms = role?.permissions || {};
  const modules = Object.keys(perms).sort();
  if (!modules.length) return <div className="text-sm" style={{ color: HC.soft }}>No permissions</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {modules.map((m) => (
        <div key={m} className="rounded-lg border p-2 text-xs" style={{ borderColor: HC.border, background: HC.canvas }}>
          <div className="font-semibold capitalize" style={{ color: HC.text }}>{m.replace(/_/g, " ")}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(perms[m] || []).map((a) => (
              <span key={a} className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                    style={{ background: HC.burgundySoft, color: HC.burgundy }}>{a}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function IAM() {
  const [tab, setTab] = useState("users");
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // dialogs
  const [provOpen, setProvOpen] = useState(false);
  const [provForm, setProvForm] = useState(emptyProv);
  const [provResult, setProvResult] = useState(null);       // { temp_password, email, ... }

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);           // full row
  const [editForm, setEditForm] = useState({});

  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [inspectRole, setInspectRole] = useState(null);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinResult, setPinResult] = useState(null);

  const savePin = async (mode) => {
    try {
      const body = mode === "generate" ? { generate: true }
        : mode === "clear" ? { pin: null } : { pin: pinTarget.pin };
      const r = await api.post(`/hr/iam/users/${pinTarget.id}/pos-pin`, body);
      setPinResult(r.pin ? `POS PIN for ${pinTarget.name}: ${r.pin}` : `POS access revoked for ${pinTarget.name}`);
      setPinTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reload = async () => {
    try {
      const [sum, u, r, reg, loc, unl] = await Promise.all([
        api.get("/hr/iam/summary"),
        api.get("/hr/iam/users", { params: {
          q: search || undefined,
          role_id: roleFilter || undefined,
          status: statusFilter || undefined,
        }}),
        api.get("/hr/iam/roles"),
        api.get("/hr/iam/regions"),
        api.get("/locations"),
        api.get("/hr/iam/employees-unlinked"),
      ]);
      setSummary(sum);
      setUsers(u);
      setRoles(r);
      setRegions(reg);
      setLocations(loc);
      setUnlinked(unl);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search, roleFilter, statusFilter]);

  const roleById = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);
  const activeRole = roleById[provForm.role_id];
  const scopeNeeded = activeRole?.scope;                    // 'global' | 'region' | 'branch'
  const editRole = roleById[editForm.role_id];
  const editScope = editRole?.scope;

  const provision = async () => {
    try {
      const payload = { ...provForm };
      if (!payload.email || !payload.name || !payload.role_id) {
        toast.error("Email, name and role are required"); return;
      }
      if (scopeNeeded === "branch" && !payload.location_id) { toast.error("Branch-scoped roles need a branch"); return; }
      if (scopeNeeded === "region" && !payload.region_id) { toast.error("Regional roles need a region"); return; }
      if (scopeNeeded === "global") { payload.location_id = null; payload.region_id = null; }
      const res = await api.post("/hr/iam/users/provision", payload);
      setProvResult(res);
      setProvForm(emptyProv);
      toast.success(`Provisioned ${res.email}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      email: u.email, name: u.name, phone: u.phone || "",
      role_id: u.role_id, location_id: u.location_id || "",
      region_id: u.region_id || "", employee_id: u.employee_id || "",
      pin: u.pin || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (editScope === "global") { payload.location_id = null; payload.region_id = null; }
      if (editScope === "region" && !payload.region_id) { toast.error("Regional role needs a region"); return; }
      if (editScope === "branch" && !payload.location_id) { toast.error("Branch-scoped role needs a branch"); return; }
      await api.put(`/hr/iam/users/${editUser.id}`, payload);
      toast.success(`Updated ${editUser.email}`);
      setEditOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetPassword = async () => {
    try {
      const res = await api.post(`/hr/iam/users/${resetTarget.id}/reset-password`, {});
      setResetResult({ ...res, email: resetTarget.email });
      toast.success(`Reset password for ${resetTarget.email}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setResetTarget(null);
  };
  const doDeactivate = async () => {
    try {
      await api.post(`/hr/iam/users/${deactivateTarget.id}/deactivate`, {});
      toast.success(`Deactivated ${deactivateTarget.email}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    setDeactivateTarget(null);
  };
  const activate = async (u) => {
    try { await api.post(`/hr/iam/users/${u.id}/activate`, {}); toast.success(`Activated ${u.email}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const showHistory = async (u) => {
    setHistoryUser(u);
    try { const rows = await api.get(`/hr/iam/users/${u.id}/history`); setHistoryRows(rows); }
    catch (e) { setHistoryRows([]); toast.error(formatApiError(e)); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  return (
    <div className="space-y-5" data-testid="iam-page">
      {/* Header stats */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatBox label="TOTAL USERS" value={summary.total} testId="iam-stat-total" />
          <StatBox label="ACTIVE" value={summary.active} tint="#166534" testId="iam-stat-active" />
          <StatBox label="INACTIVE" value={summary.inactive} tint="#A52222" testId="iam-stat-inactive" />
          <StatBox label="LINKED TO EMP." value={summary.linked_to_employee} testId="iam-stat-linked" />
          <StatBox label="ON LEGACY ROLE" value={summary.on_deprecated_role} tint={summary.on_deprecated_role ? "#A52222" : HC.text} testId="iam-stat-legacy" />
          <StatBox label="NO ROLE" value={summary.unassigned_role} tint={summary.unassigned_role ? "#A52222" : HC.text} testId="iam-stat-unassigned" />
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <SectionHead title="Identity & Access Management" sub="Provision, reassign roles, reset passwords and audit every login in one place." />
          <div className="flex-1" />
          <Btn onClick={() => { setProvResult(null); setProvForm(emptyProv); setProvOpen(true); }} data-testid="iam-provision-btn">
            <UserPlus size={15} className="mr-1" /> Provision User
          </Btn>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="iam-tabs">
            <TabsTrigger value="users" data-testid="iam-tab-users">Users</TabsTrigger>
            <TabsTrigger value="roles" data-testid="iam-tab-roles">Roles</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="iam-tab-assignments">Assignments</TabsTrigger>
            <TabsTrigger value="portal" data-testid="iam-tab-portal">Staff Portal Access</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="flex items-center gap-2 border rounded-lg h-9 px-3 bg-white min-w-[260px]" style={{ borderColor: HC.border }}>
                <Search size={14} style={{ color: HC.soft }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email…" className="bg-transparent flex-1 outline-none text-sm" data-testid="iam-search" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter} data-testid="iam-role-filter">
                <option value="">All roles</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter} data-testid="iam-status-filter">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
              <button onClick={reload} className="ml-auto text-xs font-semibold flex items-center gap-1" style={{ color: HC.burgundy }} data-testid="iam-refresh">
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            {users.length === 0 ? <Empty label="No users found." /> : (
              <Table
                columns={[
                  { key: "user", label: "User", render: (u) => (
                    <div>
                      <div className="font-semibold" style={{ color: HC.text }}>{u.name}</div>
                      <div className="text-[11px]" style={{ color: HC.soft }}>{u.email}</div>
                    </div>
                  )},
                  { key: "role", label: "Role", render: (u) => (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-semibold" style={{ color: HC.text }}>{u.role_name}</span>
                      {u.role_deprecated && <Pill tone="red">Legacy</Pill>}
                      {u.role_is_admin && <Pill tone="gold">Admin</Pill>}
                    </div>
                  )},
                  { key: "scope", label: "Scope", render: (u) => (
                    <div className="text-xs">
                      <div style={{ color: HC.text }}>{u.role_scope === "region" ? u.region_name : u.role_scope === "branch" ? u.location_name : "Cross-org"}</div>
                      {u.employee_number && <div className="text-[11px]" style={{ color: HC.soft }}>Emp #{u.employee_number}</div>}
                    </div>
                  )},
                  { key: "status", label: "Status", render: (u) => <Pill tone={u.status === "active" ? "green" : "red"}>{u.status}</Pill> },
                  { key: "last", label: "Last login", render: (u) => (
                    <div className="text-[11px]" style={{ color: HC.soft }}>
                      {u.last_login ? fmtDateTime(u.last_login) : "Never"}
                    </div>
                  )},
                  { key: "actions", label: "", render: (u) => (
                    <div className="flex items-center gap-1 justify-end">
                      <button title="Edit" onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-[#FBF8F2]" data-testid={`iam-user-edit-${u.id}`}><Pencil size={14} /></button>
                      <button title="Reset password" onClick={() => setResetTarget(u)} className="p-1.5 rounded hover:bg-[#FBF8F2]" data-testid={`iam-user-reset-${u.id}`}><KeyRound size={14} /></button>
                      <button title={u.has_pos_pin ? "Manage POS PIN (set)" : "Issue POS PIN"} onClick={() => setPinTarget({ ...u, pin: "" })}
                        className="p-1.5 rounded hover:bg-[#FBF8F2]" style={{ color: u.has_pos_pin ? "#166534" : HC.soft }}
                        data-testid={`iam-user-pos-pin-${u.id}`}><ShieldCheck size={14} /></button>
                      <button title="History" onClick={() => showHistory(u)} className="p-1.5 rounded hover:bg-[#FBF8F2]" data-testid={`iam-user-history-${u.id}`}><History size={14} /></button>
                      {u.status === "active"
                        ? <button title="Deactivate" onClick={() => setDeactivateTarget(u)} className="p-1.5 rounded hover:bg-[#FBF8F2]" style={{ color: "#A52222" }} data-testid={`iam-user-deactivate-${u.id}`}><Power size={14} /></button>
                        : <button title="Activate" onClick={() => activate(u)} className="p-1.5 rounded hover:bg-[#FBF8F2]" style={{ color: "#166534" }} data-testid={`iam-user-activate-${u.id}`}><Power size={14} /></button>}
                    </div>
                  )},
                ]}
                rows={users}
                testId="iam-users-table"
              />
            )}
          </TabsContent>

          <TabsContent value="roles" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {roles.map((r) => (
                <button key={r.id} onClick={() => setInspectRole(r)} className="text-left rounded-2xl border p-4 hover:shadow-md transition"
                        style={{ borderColor: HC.border, background: HC.panel }} data-testid={`iam-role-card-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-brand text-lg font-bold" style={{ color: HC.text }}>{r.name}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: HC.soft }}>{r.description}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {r.is_admin && <Pill tone="gold">Admin</Pill>}
                      <Pill tone={r.scope === "global" ? "green" : r.scope === "region" ? "gold" : "grey"}>{r.scope || "—"}</Pill>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {Object.keys(r.permissions || {}).slice(0, 8).map((m) => (
                      <span key={m} className="text-[10px] px-1.5 py-0.5 rounded font-semibold capitalize" style={{ background: HC.canvas, color: HC.muted }}>{m}</span>
                    ))}
                    {Object.keys(r.permissions || {}).length > 8 && <span className="text-[10px]" style={{ color: HC.soft }}>+ {Object.keys(r.permissions).length - 8} more</span>}
                  </div>
                  <div className="mt-3 text-[11px] font-semibold" style={{ color: HC.burgundy }}>
                    Tier {r.tier} · {(r.permissions || {}) ? Object.keys(r.permissions).length : 0} modules
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="assignments" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl border p-4" style={{ borderColor: HC.border, background: HC.panel }}>
                <div className="text-[11px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>BY ROLE</div>
                <div className="space-y-1.5">
                  {summary?.by_role?.map((row) => (
                    <div key={row.role_id} className="flex items-center justify-between text-xs">
                      <span style={{ color: HC.text }} className={row.deprecated ? "line-through" : ""}>{row.role_name}</span>
                      <span className="font-brand font-bold" style={{ color: HC.burgundy }}>{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: HC.border, background: HC.panel }}>
                <div className="text-[11px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>BY REGION</div>
                <div className="space-y-1.5">
                  {summary?.by_region?.map((row) => (
                    <div key={row.region_id || "none"} className="flex items-center justify-between text-xs">
                      <span style={{ color: HC.text }}>{row.region_name}</span>
                      <span className="font-brand font-bold" style={{ color: HC.burgundy }}>{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: HC.border, background: HC.panel }}>
                <div className="text-[11px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>BY BRANCH</div>
                <div className="space-y-1.5">
                  {summary?.by_branch?.map((row) => (
                    <div key={row.location_id} className="flex items-center justify-between text-xs">
                      <span style={{ color: HC.text }}>{row.location_name}</span>
                      <span className="font-brand font-bold" style={{ color: HC.burgundy }}>{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="portal" className="mt-4">
            <StaffPortalAccess />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Provision dialog */}
      <Dialog open={provOpen} onOpenChange={setProvOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-brand" style={{ color: HC.text }}>Provision new user</DialogTitle>
            <DialogDescription>Create a login, assign an IAM v2 role, and (optionally) link it to an employee record. A one-time password is generated for you.</DialogDescription>
          </DialogHeader>

          {provResult ? (
            <div className="p-4 rounded-lg border" style={{ borderColor: HC.gold, background: "#FFFBEA" }} data-testid="iam-prov-result">
              <div className="flex items-center gap-2 mb-2" style={{ color: HC.burgundy }}>
                <ShieldCheck size={18} /> <span className="font-brand font-bold">User created</span>
              </div>
              <div className="text-sm" style={{ color: HC.text }}>Email: <span className="font-semibold">{provResult.email}</span></div>
              <div className="text-sm mt-1" style={{ color: HC.text }}>Role: <span className="font-semibold">{provResult.role_name}</span> ({provResult.role_scope})</div>
              <div className="mt-3 flex items-center gap-2 p-3 rounded" style={{ background: "#fff" }}>
                <span className="text-xs font-semibold" style={{ color: HC.soft }}>Temp password</span>
                <code className="flex-1 font-mono text-sm" style={{ color: HC.burgundy }}>{provResult.temp_password}</code>
                <button onClick={() => copy(provResult.temp_password)} className="p-1.5 rounded hover:bg-[#FBF8F2]" title="Copy" data-testid="iam-prov-copy">
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-[11px] mt-2" style={{ color: HC.soft }}>Share this once with the user — they will be prompted to reset it on first login. This password will not be shown again.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name *"><Input value={provForm.name} onChange={(e) => setProvForm({ ...provForm, name: e.target.value })} data-testid="iam-prov-name" /></Field>
              <Field label="Email *"><Input type="email" value={provForm.email} onChange={(e) => setProvForm({ ...provForm, email: e.target.value })} data-testid="iam-prov-email" /></Field>
              <Field label="Phone"><Input value={provForm.phone} onChange={(e) => setProvForm({ ...provForm, phone: e.target.value })} data-testid="iam-prov-phone" /></Field>
              <Field label="Role *">
                <Select value={provForm.role_id} onValueChange={(v) => setProvForm({ ...provForm, role_id: v, location_id: "", region_id: "" })} data-testid="iam-prov-role">
                  <option value="">Pick a role…</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}  ({r.scope})</option>)}
                </Select>
              </Field>
              {scopeNeeded === "region" && (
                <Field label="Region *">
                  <Select value={provForm.region_id} onValueChange={(v) => setProvForm({ ...provForm, region_id: v })} data-testid="iam-prov-region">
                    <option value="">Pick a region…</option>
                    {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Select>
                </Field>
              )}
              {scopeNeeded === "branch" && (
                <Field label="Branch *">
                  <Select value={provForm.location_id} onValueChange={(v) => setProvForm({ ...provForm, location_id: v })} data-testid="iam-prov-branch">
                    <option value="">Pick a branch…</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Select>
                </Field>
              )}
              <Field label="Link to employee (optional)">
                <Select value={provForm.employee_id} onValueChange={(v) => setProvForm({ ...provForm, employee_id: v })} data-testid="iam-prov-employee">
                  <option value="">— Not linked —</option>
                  {unlinked.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.employee_number || e.position_title || "—"}</option>)}
                </Select>
              </Field>
              <Field label="POS PIN (optional)"><Input value={provForm.pin} onChange={(e) => setProvForm({ ...provForm, pin: e.target.value })} maxLength={6} data-testid="iam-prov-pin" /></Field>
              <Field label="Custom temp password (optional)"><Input value={provForm.password} onChange={(e) => setProvForm({ ...provForm, password: e.target.value })} placeholder="Leave blank to auto-generate" data-testid="iam-prov-password" /></Field>
            </div>
          )}

          <DialogFooter>
            {provResult
              ? <Btn onClick={() => setProvOpen(false)} data-testid="iam-prov-done">Done</Btn>
              : <>
                  <button onClick={() => setProvOpen(false)} className="px-3 h-9 rounded-lg text-sm font-semibold" style={{ color: HC.muted }}>Cancel</button>
                  <Btn onClick={provision} data-testid="iam-prov-save"><UserPlus size={14} className="mr-1" /> Provision</Btn>
                </>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-brand" style={{ color: HC.text }}>Edit user</DialogTitle>
            <DialogDescription>Change role, reassign region/branch or link to an employee record. Passwords change through the reset action.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Full name"><Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="iam-edit-name" /></Field>
            <Field label="Email"><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="iam-edit-email" /></Field>
            <Field label="Phone"><Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="iam-edit-phone" /></Field>
            <Field label="Role">
              <Select value={editForm.role_id || ""} onValueChange={(v) => setEditForm({ ...editForm, role_id: v, location_id: "", region_id: "" })} data-testid="iam-edit-role">
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}  ({r.scope})</option>)}
              </Select>
            </Field>
            {editScope === "region" && (
              <Field label="Region">
                <Select value={editForm.region_id || ""} onValueChange={(v) => setEditForm({ ...editForm, region_id: v })} data-testid="iam-edit-region">
                  <option value="">Pick a region…</option>
                  {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </Field>
            )}
            {editScope === "branch" && (
              <Field label="Branch">
                <Select value={editForm.location_id || ""} onValueChange={(v) => setEditForm({ ...editForm, location_id: v })} data-testid="iam-edit-branch">
                  <option value="">Pick a branch…</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select>
              </Field>
            )}
            <Field label="POS PIN"><Input value={editForm.pin || ""} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} maxLength={6} data-testid="iam-edit-pin" /></Field>
          </div>
          <DialogFooter>
            <button onClick={() => setEditOpen(false)} className="px-3 h-9 rounded-lg text-sm font-semibold" style={{ color: HC.muted }}>Cancel</button>
            <Btn onClick={saveEdit} data-testid="iam-edit-save">Save changes</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role inspect */}
      <Dialog open={!!inspectRole} onOpenChange={() => setInspectRole(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-brand" style={{ color: HC.text }}>{inspectRole?.name}</DialogTitle>
            <DialogDescription>{inspectRole?.description}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mb-3">
            <Pill tone={inspectRole?.scope === "global" ? "green" : inspectRole?.scope === "region" ? "gold" : "grey"}>{inspectRole?.scope}</Pill>
            <Pill tone="grey">Tier {inspectRole?.tier}</Pill>
            {inspectRole?.is_admin && <Pill tone="gold">Admin bypass</Pill>}
            {inspectRole?.deprecated && <Pill tone="red">Deprecated</Pill>}
          </div>
          <PermMatrix role={inspectRole} />
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyUser} onOpenChange={() => { setHistoryUser(null); setHistoryRows([]); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-brand" style={{ color: HC.text }}>History · {historyUser?.name}</DialogTitle>
            <DialogDescription>Every IAM audit event for this account.</DialogDescription>
          </DialogHeader>
          {historyRows.length === 0 ? <Empty label="No history yet." /> : (
            <div className="max-h-[420px] overflow-y-auto text-sm divide-y" style={{ borderColor: HC.border }}>
              {historyRows.map((r, i) => (
                <div key={i} className="py-2.5 flex items-start gap-3">
                  <div className="mt-0.5"><ChevronDown size={14} style={{ color: HC.soft }} /></div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold" style={{ color: HC.text }}>{r.action} · {r.module}</div>
                    <div className="text-[11px]" style={{ color: HC.soft }}>{r.actor_email || r.actor_id} · {fmtDateTime(r.timestamp)}</div>
                    {r.notes && <div className="text-[11px] mt-0.5" style={{ color: HC.muted }}>{r.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password confirm */}
      <AlertDialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password for {resetTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>A new temp password will be generated. The user will be prompted to change it on first login.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={resetPassword} data-testid="iam-reset-confirm">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetResult} onOpenChange={() => setResetResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand">Password reset</DialogTitle>
            <DialogDescription>{resetResult?.email}</DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded flex items-center gap-2" style={{ background: "#fff", border: `1px solid ${HC.gold}` }}>
            <code className="flex-1 font-mono text-sm" style={{ color: HC.burgundy }}>{resetResult?.temp_password}</code>
            <button onClick={() => copy(resetResult?.temp_password)} className="p-1.5 rounded hover:bg-[#FBF8F2]" data-testid="iam-reset-copy"><Copy size={14} /></button>
          </div>
          <p className="text-[11px]" style={{ color: HC.soft }}>Deliver this once — it will not be shown again.</p>
          <DialogFooter>
            <Btn onClick={() => setResetResult(null)} data-testid="iam-reset-done">Done</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deactivateTarget} onOpenChange={() => setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {deactivateTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>They will not be able to sign in until reactivated.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeactivate} data-testid="iam-deactivate-confirm">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pinTarget} onOpenChange={() => setPinTarget(null)}>
        <DialogContent className="max-w-md" data-testid="iam-pos-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand">POS PIN · {pinTarget?.name}</DialogTitle>
            <DialogDescription>
              {pinTarget?.has_pos_pin ? "A PIN is already set. Set a new one, auto-generate, or revoke POS access."
                : "This user has no POS PIN yet — issue one so they can sign in at a till."}
            </DialogDescription>
          </DialogHeader>
          <Field label="New 4-digit PIN">
            <Input data-testid="iam-pos-pin-input" inputMode="numeric" maxLength={4} value={pinTarget?.pin || ""}
              onChange={(e) => setPinTarget({ ...pinTarget, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
          </Field>
          <DialogFooter className="flex-wrap gap-2">
            <Btn variant="outline" onClick={() => savePin("generate")} data-testid="iam-pos-pin-generate">Auto-generate</Btn>
            {pinTarget?.has_pos_pin && (
              <Btn variant="outline" onClick={() => savePin("clear")} data-testid="iam-pos-pin-revoke">Revoke POS access</Btn>
            )}
            <Btn onClick={() => savePin("set")} disabled={(pinTarget?.pin || "").length !== 4} data-testid="iam-pos-pin-save">Set PIN</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinResult} onOpenChange={() => setPinResult(null)}>
        <DialogContent className="max-w-md" data-testid="iam-pos-pin-result">
          <DialogHeader><DialogTitle className="font-brand">POS PIN updated</DialogTitle></DialogHeader>
          <div className="p-3 rounded flex items-center gap-2" style={{ background: "#fff", border: `1px solid ${HC.gold}` }}>
            <code className="flex-1 font-mono text-sm" style={{ color: HC.burgundy }}>{pinResult}</code>
          </div>
          <p className="text-[11px]" style={{ color: HC.soft }}>Deliver this once — PINs are never displayed again.</p>
          <DialogFooter><Btn onClick={() => setPinResult(null)} data-testid="iam-pos-pin-done">Done</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
